import type { AuditEntry } from '@smarton-pbx/shared';
import { ScrollText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { type Column, Cells, TableHead, useTable } from '../components/table';
import { EmptyState, Loading, Notice, PageHeader } from '../components/ui';
import { locale, t, tp } from '../i18n';
import { when } from './Calls';

const PAGE = 100;

/** What each entry means, in words. Unknown actions show as they are. */
const actionNames: Record<string, string> = {
  'recording.played': t('Played a recording'),
  'recording.downloaded': t('Downloaded a recording'),
  'recording.paused': t('Paused recording during a call'),
  'recording.resumed': t('Resumed recording during a call'),
  'voicemail.listened': t('Listened to a voicemail'),
  'call.declined': t('Declined a call'),
  'review.outcome-corrected': t('Corrected a call’s outcome'),
  'review.reasons-corrected': t('Corrected a call’s reasons'),
  'transcript.corrected': t('Corrected a transcript line'),
  'transcript.checked': t('Marked a transcript as checked'),
  'transcript.rerun': t('Transcribed a call again'),
  'retention.deleted': t('Deleted old recordings'),
  'user.created': t('Added a person'),
  'user.updated': t('Changed a person'),
  'user.password-link-created': t('Made a password link'),
  'user.mfa-reset': t('Reset two-step sign-in'),
  'table.exported': t('Downloaded a table as Excel'),
  'user.photo-set': t('Set a photo'),
  'user.photo-removed': t('Removed a photo'),
  'password.set': t('Set their password'),
  'password.changed': t('Changed their password'),
  'mfa.enabled': t('Turned on two-step sign-in'),
  'region.created': t('Added a region'),
  'region.updated': t('Changed a region'),
  'region.deleted': t('Removed a region'),
  'number.created': t('Added a number'),
  'number.updated': t('Changed a number'),
  'number.deleted': t('Removed a number'),
  'trunk.created': t('Added a carrier'),
  'trunk.updated': t('Changed a carrier'),
  'trunk.deleted': t('Removed a carrier'),
  'settings.calls-updated': t('Changed call settings'),
  'settings.notifications.updated': t('Changed notification defaults'),
  'settings.transcription.updated': t('Changed transcription settings'),
  'settings.analysis.updated': t('Changed AI analysis settings'),
  'settings.recording-notice-updated': t('Changed the recording notice'),
  'announcement.uploaded': t('Uploaded an announcement'),
  'announcement.deleted': t('Removed an announcement'),
  'category.created': t('Added a reason category'),
  'category.updated': t('Changed a reason category'),
  'branding.updated': t('Changed branding'),
  'branding.image-uploaded': t('Uploaded a logo or icon'),
  'branding.image-removed': t('Removed a logo or icon'),
  'tenant.created': t('Created the account'),
  'tenant.updated': t('Changed the account'),
  'tenant.admin-created': t('Added an administrator'),
  'tenant.image-uploaded': t('Uploaded a logo or icon'),
  'tenant.suspend': t('Suspended the account'),
  'tenant.activate': t('Reactivated the account'),
  'lead.created': t('Added a lead'),
  'lead.deleted': t('Deleted a lead'),
  'lead.exported': t('Exported the pipeline'),
};

const groups = [
  { value: '', label: t('Everything') },
  { value: 'recording.', label: t('Recordings') },
  { value: 'voicemail.', label: t('Voicemail') },
  { value: 'review.', label: t('AI review corrections') },
  { value: 'transcript.', label: t('Transcripts') },
  { value: 'retention.', label: t('Automatic deletion') },
  { value: 'user.', label: t('People') },
  { value: 'settings.', label: t('Settings') },
  { value: 'number.', label: t('Numbers') },
  { value: 'trunk.', label: t('Carriers') },
  { value: 'region.', label: t('Regions') },
  { value: 'lead.', label: t('Pipeline') },
  { value: 'table.', label: t('Table downloads') },
  { value: 'tenant.', label: t('Platform staff') },
];

function actor(entry: AuditEntry): string {
  if (entry.actorType === 'SYSTEM') return t('The system');
  if (entry.actorType === 'PLATFORM_ADMIN') return t('{name} (platform staff)', { name: entry.actorName ?? t('Platform staff') });
  return entry.actorName ?? t('A removed person');
}

/** A short line about the details: what was deleted, or which settings changed. */
function describe(entry: AuditEntry): string | null {
  const details = entry.details as Record<string, unknown> | null;
  if (!details || typeof details !== 'object') return null;
  if (entry.action === 'table.exported') return `${String(details.table)} · ${tp(Number(details.rows ?? 0), '{n} row', '{n} rows')}`;
  if (entry.action === 'retention.deleted') {
    const n = (key: string) => Number(details[key] ?? 0);
    return [
      tp(n('recordings'), '{n} recording', '{n} recordings'),
      tp(n('transcripts'), '{n} transcript', '{n} transcripts'),
      tp(n('reviews'), '{n} AI review', '{n} AI reviews'),
      tp(n('voicemails'), '{n} voicemail', '{n} voicemails'),
    ].join(', ');
  }
  const parts = Object.entries(details)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => {
      const shown = Array.isArray(value) ? value.join(', ') || t('none') : value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value);
      return `${key}: ${shown.length > 60 ? `${shown.slice(0, 57)}…` : shown}`;
    });
  const text = parts.join(' · ');
  return text.length > 240 ? `${text.slice(0, 237)}…` : text || null;
}

function auditColumns(): Column<AuditEntry>[] {
  return [
    {
      key: 'when',
      label: t('When'),
      fixed: true,
      className: 'muted',
      cell: (entry) => (
        <span style={{ whiteSpace: 'nowrap' }} title={new Date(entry.createdAt).toLocaleString(locale)}>
          {when(entry.createdAt)}
        </span>
      ),
      value: (entry) => new Date(entry.createdAt),
    },
    { key: 'who', label: t('Who'), className: 'audit-actor', cell: actor, value: actor },
    {
      key: 'what',
      label: t('What'),
      cell: (entry) => (
        <>
          {actionNames[entry.action] ?? entry.action}
          {entry.targetType === 'call' && entry.targetId && (
            <>
              {' · '}
              <Link to={`/calls/${entry.targetId}`}>{t('the call')}</Link>
            </>
          )}
        </>
      ),
      value: (entry) => actionNames[entry.action] ?? entry.action,
    },
    { key: 'details', label: t('Details'), className: 'hide-mobile audit-details', cell: describe, value: describe },
  ];
}

/** Every entry of the group, page by page, for a download: up to 10 000. */
async function allEntries(group: string): Promise<AuditEntry[]> {
  const rows: AuditEntry[] = [];
  for (;;) {
    const page = await api.audit({ ...(group && { action: group }), ...(rows.length && { before: rows.at(-1)!.createdAt }), limit: 200 });
    rows.push(...page);
    if (page.length < 200 || rows.length >= 10_000) return rows;
  }
}

/**
 * Who did what: recordings played and downloaded, recording paused, settings
 * and people changed, platform staff's changes, and what the system deleted.
 */
export function AuditLog() {
  const [group, setGroup] = useState('');
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string>();
  // Only admins come here.
  const table = useTable('audit-log', auditColumns(), { name: t('Audit log'), rows: () => allEntries(group) });

  useEffect(() => {
    let current = true;
    setEntries(null);
    setError(undefined);
    api.audit({ ...(group && { action: group }), limit: PAGE }).then(
      (rows) => {
        if (!current) return;
        setEntries(rows);
        setMore(rows.length === PAGE);
      },
      (err: unknown) => current && setError(err instanceof Error ? err.message : String(err)),
    );
    return () => {
      current = false;
    };
  }, [group]);

  async function loadMore() {
    if (!entries?.length) return;
    setLoadingMore(true);
    try {
      const rows = await api.audit({ ...(group && { action: group }), before: entries.at(-1)!.createdAt, limit: PAGE });
      setEntries([...entries, ...rows]);
      setMore(rows.length === PAGE);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="stack">
      <PageHeader title={t('Audit log')} description={t('Who played recordings, changed settings or managed people, and what was deleted automatically.')} />
      <div className="card filters">
        <select value={group} onChange={(e) => setGroup(e.target.value)} aria-label={t('Show')} data-testid="audit-group">
          {groups.map((g) => (
            <option key={g.value} value={g.value}>
              {g.label}
            </option>
          ))}
        </select>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {!entries ? (
        error ? null : <Loading />
      ) : entries.length === 0 ? (
        <div className="card">
          <EmptyState icon={ScrollText} title={t('Nothing here yet')}>
            <p>{t('Entries appear as people use the system.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table className="calls-table">
            <TableHead table={table} />
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} data-testid="audit-entry" data-action={entry.action}>
                  <Cells table={table} row={entry} />
                  <td className="actions" />
                </tr>
              ))}
            </tbody>
          </table>
          {more && (
            <div className="card-footer" style={{ justifyContent: 'center' }}>
              <button type="button" className="secondary" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? t('Loading…') : t('Show more')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
